import { AlertCircle } from "lucide-react-native";
import type { ComponentRef, ReactNode } from "react";
import { Box } from "../../../../ui/box";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "../../../../ui/form-control";
import type { View } from "react-native";
import type { FormFieldSpec } from "./formTypes";

type Props = {
  field: FormFieldSpec;
  error: string | undefined;
  /** The control draws its own label (a checkbox does). */
  hideLabel: boolean;
  /** The card keeps these to focus the first invalid field after a refused submit. */
  register: (key: string, node: ComponentRef<typeof View> | null) => void;
  children: ReactNode;
};

/**
 * Label, hint and error around one control.
 *
 * The wrapper is focusable (but out of the tab order) so that after a refused submit
 * the card can move real focus to the first field with a problem: on web that scrolls
 * it into view and a screen reader lands on its label and its error together.
 */
const FieldShell = ({ field, error, hideLabel, register, children }: Props) => (
  <FormControl
    isInvalid={!!error}
    isRequired={field.required}
    className={field.width === "half" ? "min-w-0 flex-1" : "w-full"}
  >
    <Box
      ref={(node) => register(field.key, node)}
      focusable
      tabIndex={-1}
      className="gap-1.5"
    >
      {hideLabel ? null : (
        <FormControlLabel>
          <FormControlLabelText>{field.label}</FormControlLabelText>
        </FormControlLabel>
      )}
      {field.hint ? (
        <FormControlHelper>
          <FormControlHelperText>{field.hint}</FormControlHelperText>
        </FormControlHelper>
      ) : null}
      {children}
      <FormControlError accessibilityLiveRegion="polite">
        <FormControlErrorIcon as={AlertCircle} className="mr-1" />
        <FormControlErrorText>{error}</FormControlErrorText>
      </FormControlError>
    </Box>
  </FormControl>
);

export default FieldShell;
